import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import CubicSignShape from "../../../../components/visuals/CubicSignShape";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function polyToLatex(coeffs) {
  const degree = coeffs.length - 1;
  const parts = [];

  coeffs.forEach((coef, idx) => {
    if (coef === 0) return;
    const power = degree - idx;
    const absCoef = Math.abs(coef);
    let term = "";

    if (power === 0) {
      term = String(absCoef);
    } else if (power === 1) {
      term = absCoef === 1 ? "x" : `${absCoef}x`;
    } else {
      term = absCoef === 1 ? `x^${power}` : `${absCoef}x^${power}`;
    }

    if (parts.length === 0) {
      parts.push(coef < 0 ? `-${term}` : term);
    } else {
      parts.push(coef < 0 ? `- ${term}` : `+ ${term}`);
    }
  });

  return parts.length ? parts.join(" ") : "0";
}

function evaluate(coeffs, x) {
  return coeffs.reduce((acc, coef) => acc * x + coef, 0);
}

function divisorsOf(n) {
  const absN = Math.abs(n);
  const positives = [];
  for (let d = 1; d <= absN; d += 1) {
    if (absN % d === 0) positives.push(d);
  }
  const ordered = [];
  positives.forEach((d) => {
    ordered.push(d, -d);
  });
  return ordered;
}

function divideByRoot(coeffs, root) {
  const out = [coeffs[0]];
  for (let i = 1; i < coeffs.length - 1; i += 1) {
    out.push(coeffs[i] + out[out.length - 1] * root);
  }
  return out;
}

function quarticValue(a, b, c, d, e, x) {
  return (((a * x + b) * x + c) * x + d) * x + e;
}

function buildQuarticTask() {
  const rootTriples = [
    [-3, 1, 2],
    [-2, -1, 3],
    [-3, -2, 2],
    [-3, 0, 2],
    [-2, 0, 3],
    [-1, 0, 2],
  ];

  for (let attempt = 0; attempt < 800; attempt += 1) {
    const [r1, r2, r3] = choice(rootTriples).slice().sort((a, b) => a - b);
    const s = choice([-1, 1]);
    const e = randInt(-3, 3);

    const sum = r1 + r2 + r3;
    const pairwise = r1 * r2 + r1 * r3 + r2 * r3;
    const product = r1 * r2 * r3;

    // f'(x) = 4s(x-r1)(x-r2)(x-r3), so f(x) has integer coefficients
    const a4 = s;
    const a3 = (-4 * s * sum) / 3;
    const a2 = 2 * s * pairwise;
    const a1 = -4 * s * product;
    const a0 = e;

    if (![a4, a3, a2, a1, a0].every(Number.isInteger)) continue;

    const d3 = 4 * s;
    const d2 = -4 * s * sum;
    const d1 = 4 * s * pairwise;
    const d0 = -4 * s * product;

    const y1 = quarticValue(a4, a3, a2, a1, a0, r1);
    const y2 = quarticValue(a4, a3, a2, a1, a0, r2);
    const y3 = quarticValue(a4, a3, a2, a1, a0, r3);

    if ([y1, y2, y3].some((v) => Math.abs(v) > 140)) continue;

    const leftSign = s > 0 ? "-" : "+";
    const between12 = s > 0 ? "+" : "-";
    const between23 = s > 0 ? "-" : "+";
    const rightSign = s > 0 ? "+" : "-";

    let targetType;
    let targetX;
    let targetY;
    let comparisonText;

    if (s > 0) {
      targetType = "minimum globalne";
      if (y1 === y3) continue;
      if (y1 < y3) {
        targetX = r1;
        targetY = y1;
      } else {
        targetX = r3;
        targetY = y3;
      }
      comparisonText = `Porownujemy wartosci w punktach lokalnych minimow: f(${r1}) = ${y1} oraz f(${r3}) = ${y3}.`;
    } else {
      targetType = "maksimum globalne";
      if (y1 === y3) continue;
      if (y1 > y3) {
        targetX = r1;
        targetY = y1;
      } else {
        targetX = r3;
        targetY = y3;
      }
      comparisonText = `Porownujemy wartosci w punktach lokalnych maksimow: f(${r1}) = ${y1} oraz f(${r3}) = ${y3}.`;
    }

    const derivative = [d3, d2, d1, d0];
    const divisorList = divisorsOf(d0);
    const firstRoot = divisorList.find((candidate) => evaluate(derivative, candidate) === 0);
    if (firstRoot === undefined) continue;

    const firstChecks = [];
    for (const candidate of divisorList) {
      firstChecks.push({ x: candidate, value: evaluate(derivative, candidate) });
      if (candidate === firstRoot) break;
    }

    const reducedQuadratic = divideByRoot(derivative, firstRoot);
    const remainingRoots = [r1, r2, r3].filter((root, idx, arr) => root !== firstRoot || idx !== arr.indexOf(firstRoot));

    return {
      quartic: [a4, a3, a2, a1, a0],
      derivative,
      quarticLatex: polyToLatex([a4, a3, a2, a1, a0]),
      derivativeLatex: polyToLatex(derivative),
      r1,
      r2,
      r3,
      y1,
      y2,
      y3,
      leftSign,
      between12,
      between23,
      rightSign,
      targetType,
      targetX,
      targetY,
      divisorList,
      firstRoot,
      firstChecks,
      reducedQuadratic,
      remainingRoots: remainingRoots.sort((a, b) => a - b),
      comparisonText,
    };
  }

  // Safe fallback
  return {
    quartic: [-1, 0, 14, -24, 0],
    derivative: [-4, 0, 28, -24],
    quarticLatex: polyToLatex([-1, 0, 14, -24, 0]),
    derivativeLatex: polyToLatex([-4, 0, 28, -24]),
    r1: -3,
    r2: 1,
    r3: 2,
    y1: quarticValue(-1, 0, 14, -24, 0, -3),
    y2: quarticValue(-1, 0, 14, -24, 0, 1),
    y3: quarticValue(-1, 0, 14, -24, 0, 2),
    leftSign: "+",
    between12: "-",
    between23: "+",
    rightSign: "-",
    targetType: "maksimum globalne",
    targetX: -3,
    targetY: quarticValue(-1, 0, 14, -24, 0, -3),
    divisorList: [1, -1, 2, -2, 3, -3, 4, -4, 6, -6, 8, -8, 12, -12, 24, -24],
    firstRoot: 1,
    firstChecks: [{ x: 1, value: 0 }],
    reducedQuadratic: [-4, -4, 24],
    remainingRoots: [-3, 2],
    comparisonText: "Porownujemy wartosci w punktach lokalnych maksimow: f(-3) = 117 oraz f(2) = -8.",
  };
}

export function generateTask() {
  const data = buildQuarticTask();
  return {
    type: "QuarticGlobalExtremumTask",
    prompt: `Wyznacz ${data.targetType} funkcji f(x) = ${data.quarticLatex} metoda pochodnej. Podaj wspolrzedne punktu tego ekstremum.`,
    answer: `x_E=${data.targetX}, y_E=${data.targetY}`,
    data,
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz <strong>{data.targetType}</strong> funkcji:</p>
      <BlockMath math={`f(x) = ${data.quarticLatex}`} />
      <p>Podaj wspolrzedne punktu tego ekstremum.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="x_E =" /></div>
        <FractionInput
          value={value.answerX || ""}
          onChange={(next) => onChange({ ...value, answerX: next })}
          disabled={disabled}
          label="x_E"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="y_E =" /></div>
        <FractionInput
          value={value.answerY || ""}
          onChange={(next) => onChange({ ...value, answerY: next })}
          disabled={disabled}
          label="y_E"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const xValue = userAnswer.answerX?.parsed;
  const yValue = userAnswer.answerY?.parsed;
  return typeof xValue === "number"
    && typeof yValue === "number"
    && Math.abs(xValue - data.targetX) < 0.0001
    && Math.abs(yValue - data.targetY) < 0.0001;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawX = userAnswer.answerX?.raw;
  const rawY = userAnswer.answerY?.raw;
  if (!rawX && !rawY) return <i>(brak odpowiedzi)</i>;
  return (
    <div className="space-y-2">
      {rawX ? <div><InlineMath math={formatLatexPreview(rawX, "x_E")} /></div> : <div><InlineMath math={"x_E = ?"} /></div>}
      {rawY ? <div><InlineMath math={formatLatexPreview(rawY, "y_E")} /></div> : <div><InlineMath math={"y_E = ?"} /></div>}
      {rawX && rawY ? <div><InlineMath math={`E = (${formatLatexPreview(rawX)}, ${formatLatexPreview(rawY)})`} /></div> : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div><InlineMath math={`x_E = ${data.targetX}`} /></div>
      <div><InlineMath math={`y_E = ${data.targetY}`} /></div>
      <div><InlineMath math={`E = (${data.targetX}, ${data.targetY})`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const firstCheckLatex = data.firstChecks
    .map(({ x, value }) => `f'(${x}) = ${value}`)
    .join(",\\quad ");
  const reducedQuadraticLatex = polyToLatex(data.reducedQuadratic);
  const formatLinearFactor = (root) => {
    if (root === 0) return "x";
    if (root < 0) return `(x + ${Math.abs(root)})`;
    return `(x - ${root})`;
  };
  const linearFactorLatex = formatLinearFactor(data.firstRoot);

  const [dA, dB, dC, dD] = data.derivative;
  const h1 = dA;
  const h2 = dB + data.firstRoot * h1;
  const h3 = dC + data.firstRoot * h2;
  const h4 = dD + data.firstRoot * h3;

  const [qA, qB, qC] = data.reducedQuadratic;
  const delta = qB * qB - 4 * qA * qC;
  const sqrtDelta = Math.round(Math.sqrt(Math.abs(delta)));
  const denom = 2 * qA;
  const minusBValue = -qB;
  const rootFromMinus = (-qB - sqrtDelta) / denom;
  const rootFromPlus = (-qB + sqrtDelta) / denom;
  const x2Lines = String.raw`
\begin{aligned}
x_2 &= \dfrac{-b - \sqrt{\Delta}}{2a} \\[6pt]
x_2 &= \dfrac{${minusBValue} - ${sqrtDelta}}{2 \cdot ${qA}} \\[6pt]
x_2 &= ${rootFromMinus}
\end{aligned}
`;
  const x3Lines = String.raw`
\begin{aligned}
x_3 &= \dfrac{-b + \sqrt{\Delta}}{2a} \\[6pt]
x_3 &= \dfrac{${minusBValue} + ${sqrtDelta}}{2 \cdot ${qA}} \\[6pt]
x_3 &= ${rootFromPlus}
\end{aligned}
`;
  const transitionType = (leftSign, rightSign) => {
    if (leftSign === "-" && rightSign === "+") return "minimum lokalne";
    if (leftSign === "+" && rightSign === "-") return "maksimum lokalne";
    return "brak ekstremum";
  };

  const type1 = transitionType(data.leftSign, data.between12);
  const type2 = transitionType(data.between12, data.between23);
  const type3 = transitionType(data.between23, data.rightSign);

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji.</p>
      <BlockMath math={`f(x) = ${data.quarticLatex}`} />
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />

      <p><strong>Krok 2:</strong> Szukamy miejsc zerowych pochodnej wsrod dzielnikow wyrazu wolnego.</p>
      <BlockMath math={`D(f') = \\{${data.divisorList.join(",\\ ")}\\}`} />
      <BlockMath math={firstCheckLatex} />
      <BlockMath math={`x_1 = ${data.firstRoot}`} />

      <p><strong>Krok 3:</strong> Dzielimy pochodna schematem Hornera przez czynnik odpowiadajacy znalezionemu miejscu zerowemu.</p>
      <BlockMath
        math={`\\begin{array}{r|rrrr}
 & ${dA} & ${dB} & ${dC} & ${dD} \\\\ \\hline
${data.firstRoot} &  & ${data.firstRoot * h1} & ${data.firstRoot * h2} & ${data.firstRoot * h3} \\\\ \\hline
 & ${h1} & ${h2} & ${h3} & ${h4}
\\end{array}`}
      />
      <BlockMath math={`f'(x) = ${linearFactorLatex}\\left(${reducedQuadraticLatex}\\right)`} />

      <p><strong>Krok 4:</strong> Rozwiazujemy otrzymane rownanie kwadratowe z delty.</p>
      <BlockMath math={`${reducedQuadraticLatex} = 0`} />
      <BlockMath math={`a=${qA},\\ b=${qB},\\ c=${qC}`} />
      <BlockMath math={`\\Delta = b^2-4ac = (${qB})^2-4\\cdot(${qA})\\cdot(${qC}) = ${delta}`} />
      <BlockMath math={`\\sqrt{\\Delta} = \\sqrt{${delta}} = ${sqrtDelta}`} />
      <p><strong>Krok 4a:</strong> Obliczamy pierwiastki rownania kwadratowego.</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={"x_2"} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={"x_3"} />
          </div>
          <BlockMath math={x3Lines} />
        </div>
      </div>
      <p><strong>Krok 4b:</strong> Ustalamy kolejnosc miejsc zerowych pochodnej.</p>
      <BlockMath math={`x_2 = ${data.remainingRoots[0]},\\quad x_3 = ${data.remainingRoots[1]}`} />

      <p><strong>Krok 5:</strong> Sprawdzamy znak pochodnej na kolejnych przedzialach.</p>
      <div style={{ maxWidth: 560, margin: "20px auto" }}>
        <CubicSignShape
          risingRight={data.derivative[0] > 0}
          leftLabel={String(data.r1)}
          middleLabel={String(data.r2)}
          rightLabel={String(data.r3)}
          signs={[data.leftSign, data.between12, data.between23, data.rightSign]}
          width={560}
        />
      </div>
      <p>
        Dla <InlineMath math={`x < ${data.r1}`} /> znak jest <strong>{data.leftSign}</strong>,
        dla <InlineMath math={`${data.r1} < x < ${data.r2}`} /> znak jest <strong>{data.between12}</strong>,
        dla <InlineMath math={`${data.r2} < x < ${data.r3}`} /> znak jest <strong>{data.between23}</strong>,
        a dla <InlineMath math={`x > ${data.r3}`} /> znak jest <strong>{data.rightSign}</strong>.
      </p>
      <p>
        To oznacza, ze funkcja kolejno
        {" "}<strong>{data.leftSign === "+" ? "rosnie" : "maleje"}</strong>,
        potem {" "}<strong>{data.between12 === "+" ? "rosnie" : "maleje"}</strong>,
        potem {" "}<strong>{data.between23 === "+" ? "rosnie" : "maleje"}</strong>,
        a na koncu znowu {" "}<strong>{data.rightSign === "+" ? "rosnie" : "maleje"}</strong>.
      </p>

      <p><strong>Krok 6:</strong> Wyznaczamy wszystkie ekstrema lokalne i ich wspolrzedne.</p>
      <BlockMath math={`x_1 = ${data.r1}:\\ ${type1}`} />
      <BlockMath math={`x_2 = ${data.r2}:\\ ${type2}`} />
      <BlockMath math={`x_3 = ${data.r3}:\\ ${type3}`} />
      <BlockMath math={`f(${data.r1}) = ${data.y1},\\quad f(${data.r2}) = ${data.y2},\\quad f(${data.r3}) = ${data.y3}`} />
      <BlockMath math={`E_1 = (${data.r1}, ${data.y1}),\\quad E_2 = (${data.r2}, ${data.y2}),\\quad E_3 = (${data.r3}, ${data.y3})`} />

      <p><strong>Krok 7:</strong> Wybieramy <strong>{data.targetType}</strong>.</p>
      <p>{data.comparisonText}</p>
      <BlockMath math={`f(${data.r1}) = ${data.y1},\\quad f(${data.r3}) = ${data.y3}`} />
      <BlockMath math={`f(${data.targetX}) = ${data.targetY}`} />
      <BlockMath math={`x_E = ${data.targetX},\\quad y_E = ${data.targetY}`} />
      <BlockMath math={`E = (${data.targetX}, ${data.targetY})`} />
    </div>
  );
}
